/**
 * F1 — LockedFeature komponens
 *
 * Zárolt feature esetén megjelenítendő placeholder UI.
 * A feature NEM tűnik el — lakat ikonnal + CTA-val jelöljük, hogy
 * melyik csomag hozza el.
 *
 * Használat:
 *   const { active } = useFeature('projektek');
 *   if (!active) return <LockedFeature feature="projektek" />;
 */
import { useNavigate } from 'react-router-dom';
import Button from './ui/Button';
import { useFeature } from '../hooks/useFeature';
import type { FeatureName } from '../contexts/FeatureContext';

const FEATURE_LABELS: Record<FeatureName, string> = {
  szamlazo:        'Számlázó',
  hazipenztar:     'Házipénztár',
  cikkek_1arlista: 'Cikkek & Árlisták',
  partnerek:       'Partnerek',
  attekintes:      'Áttekintés',
  ajanlat:         'Ajánlatok',
  tobb_arlista:    'Több árlista',
  alap_riport:     'Alap riportok',
  projektek:       'Projektek',
  csapat:          'Csapat',
  teljes_riport:   'Teljes riportok',
  raktar:          'Raktár',
  kintlevoseg:     'Kintlévőség',
  banki_parosito:  'Banki párosító',
};

const FEATURE_PLAN_LABEL: Record<FeatureName, string> = {
  szamlazo:        'Belépő',
  hazipenztar:     'Belépő',
  cikkek_1arlista: 'Belépő',
  partnerek:       'Belépő',
  attekintes:      'Belépő',
  ajanlat:         'Plusz',
  tobb_arlista:    'Plusz',
  alap_riport:     'Plusz',
  projektek:       'Pro',
  csapat:          'Pro',
  teljes_riport:   'Pro',
  raktar:          'Raktár add-on',
  kintlevoseg:     'Kintlévőség add-on',
  banki_parosito:  'Kintlévőség add-on',
};

interface LockedFeatureProps {
  feature: FeatureName;
  /** Ha megadjuk, ez jelenik meg a feature neve helyett */
  label?: string;
  /** Kompakt megjelenítés (pl. menüpontban, kártyában) */
  compact?: boolean;
}

export default function LockedFeature({ feature, label, compact = false }: LockedFeatureProps) {
  const navigate = useNavigate();
  const { status, trialEndsAt } = useFeature(feature);

  const featureLabel = label ?? FEATURE_LABELS[feature] ?? feature;
  const planLabel = FEATURE_PLAN_LABEL[feature] ?? 'magasabb csomag';

  const isTrial = status === 'trial' && trialEndsAt && new Date(trialEndsAt) > new Date();

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1 text-gray-400 cursor-pointer hover:text-[#F47B20] transition-colors"
        onClick={() => navigate('/beallitasok/elofizetes')}
        title={`${featureLabel} — ${planLabel} csomag szükséges`}
      >
        <span style={{ fontSize: 14 }}>🔒</span>
        <span style={{ fontSize: 12 }}>{featureLabel}</span>
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '48px 24px',
        textAlign: 'center',
        minHeight: 240,
        borderRadius: 12,
        background: 'var(--color-surface, #f9fafb)',
        border: '1.5px dashed var(--color-border, #e5e7eb)',
      }}
    >
      <div style={{ fontSize: 40, lineHeight: 1 }}>🔒</div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 18, color: 'var(--color-text, #111827)', marginBottom: 6 }}>
          {featureLabel}
        </div>
        <div style={{ color: 'var(--color-text-secondary, #6b7280)', fontSize: 14, maxWidth: 360 }}>
          {isTrial
            ? `A próbaidőszak alatt kipróbálhatod — válts ${planLabel} csomagra, és a próba folytatódik.`
            : `Ez a funkció a ${planLabel} csomagtól érhető el.`}
        </div>
      </div>

      <Button
        variant="primary"
        onClick={() => navigate('/beallitasok/elofizetes')}
      >
        {isTrial ? `Próbáld ki a ${planLabel} csomagot` : `Váltás ${planLabel} csomagra`}
      </Button>
    </div>
  );
}
